// Manage which catalog categories an attribute TYPE or VALUE applies to.
// An empty bindings list means: for a type, it applies to ALL categories; for
// a value, it's available everywhere its type is (inherits the type) — same
// "empty = unrestricted" BE convention at both levels.
// Local `ids` state is edited freely and only persisted on Save via a full
// replace call, mirroring the "replace" semantics of the BE endpoints.
//
// `onBindingsLoaded` fires whenever fresh ids arrive for this target — on the
// initial open-fetch AND after a successful save — so the caller (the types
// rail / values table) can cache them for chip display ahead of the list
// refetch. GET .../types now includes boundCategoryIds at both levels, so the
// cache is a freshness override, not the chips' only source. See
// attribute-types-rail.tsx / attribute-value-row.tsx for the cache consumers.

import {
  useGetAttributeTypeCategoriesApi,
  useGetAttributeValueCategoriesApi,
  useReplaceAttributeTypeCategoriesApi,
  useReplaceAttributeValueCategoriesApi,
} from 'apis/product-attributes';
import { useGetDealCategoryTreeApi } from 'apis/supplier-prices';
import { CategoryModal } from 'components/category-picker/category-modal';
import { findCategoryPath } from 'components/category-picker/category-tree-helpers';
import type { CategoryRef } from 'components/category-picker/types';
import {
  Box,
  Button,
  Flex,
  Icon,
  IconButtonWithTooltip,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  useToast,
  VStack,
} from 'design-system';
import { useEffect, useRef, useState } from 'react';
import { FiTrash2 } from 'react-icons/fi';

export type BindingsTargetKind = 'type' | 'value';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  kind: BindingsTargetKind;
  targetId: number;
  targetLabel: string;
  onBindingsLoaded?: (ids: number[]) => void;
}

export const CategoryBindingsModal = ({ isOpen, onClose, kind, targetId, targetLabel, onBindingsLoaded }: Props) => {
  const toast = useToast();
  const [ids, setIds] = useState<number[]>([]);
  const hasPreselected = useRef(false);

  const isType = kind === 'type';

  // Only one of these is actually enabled at a time (gated on `kind`); calling
  // both keeps hook order stable instead of branching which hook to call.
  const typeQuery = useGetAttributeTypeCategoriesApi(targetId, { enabled: isOpen && isType });
  const valueQuery = useGetAttributeValueCategoriesApi(targetId, { enabled: isOpen && !isType });
  const { data, isLoading } = isType ? typeQuery : valueQuery;

  const { data: treeData, isLoading: isTreeLoading } = useGetDealCategoryTreeApi({ enabled: isOpen });
  const tree = treeData?.data ?? [];

  // Reset when the modal closes.
  useEffect(() => {
    if (!isOpen) {
      setIds([]);
      hasPreselected.current = false;
    }
  }, [isOpen]);

  // Seed local ids from the fetched bindings exactly once per open. Guarded so a
  // background refetch (the query key shares the invalidated `.../types` prefix)
  // never overwrites the user's unsaved add/remove edits.
  useEffect(() => {
    if (isOpen && data?.data && !hasPreselected.current) {
      hasPreselected.current = true;
      setIds(data.data);
      onBindingsLoaded?.(data.data);
    }
  }, [isOpen, data, onBindingsLoaded]);

  const onDone = (savedIds: number[]) => {
    toast({ title: 'Category bindings saved', status: 'success' });
    onBindingsLoaded?.(savedIds);
    onClose();
  };
  const onError = (error: any) => {
    // hooks toast server messages; this is the network-error fallback
    if (!error?.response?.data?.message) toast({ title: 'Failed to save category bindings', status: 'error' });
  };
  const replaceTypeApi = useReplaceAttributeTypeCategoriesApi({ onSuccess: onDone, onError });
  const replaceValueApi = useReplaceAttributeValueCategoriesApi({ onSuccess: onDone, onError });
  const replaceApi = isType ? replaceTypeApi : replaceValueApi;

  const addCategory = (category: CategoryRef) => {
    setIds((prev) => (prev.includes(category.id) ? prev : [...prev, category.id]));
  };
  const removeCategory = (id: number) => setIds((prev) => prev.filter((i) => i !== id));

  const save = () => {
    if (isType) replaceTypeApi.mutate({ typeId: targetId, catalogCategoryIds: ids });
    else replaceValueApi.mutate({ valueId: targetId, catalogCategoryIds: ids });
  };

  const isLoadingAny = isLoading || isTreeLoading;
  const emptyHint = isType
    ? 'No bindings — this type applies to ALL categories.'
    : "No bindings — this value is available wherever its type is (inherits the type's bindings).";

  return (
    <Modal isOpen={isOpen} onClose={onClose} size='lg' scrollBehavior='inside'>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Category Bindings — {targetLabel}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {isLoadingAny ? (
            <Flex justify='center' py='8'>
              <Spinner size='md' color='gray.400' />
            </Flex>
          ) : (
            <VStack align='stretch' spacing='2'>
              {ids.length === 0 ? (
                <Box bg='gray.50' borderRadius='md' py='4' px='3'>
                  <Text color='gray.500' fontSize='sm' textAlign='center'>
                    {emptyHint}
                  </Text>
                </Box>
              ) : (
                ids.map((id) => {
                  const path = findCategoryPath(tree, id);
                  const breadcrumb = path.length
                    ? path.map((p) => p.title || `Untitled (${p.id})`).join(' › ')
                    : `Category #${id}`;
                  return (
                    <Flex
                      key={id}
                      align='center'
                      justify='space-between'
                      borderWidth='1px'
                      borderColor='gray.200'
                      borderRadius='md'
                      px='3'
                      py='2'>
                      <Text fontSize='sm' noOfLines={1}>
                        {breadcrumb}
                      </Text>
                      <IconButtonWithTooltip
                        size='sm'
                        label='Remove'
                        aria-label='remove-category-btn'
                        Icon={<Icon as={FiTrash2} />}
                        variant='unstyled'
                        onClick={() => removeCategory(id)}
                      />
                    </Flex>
                  );
                })
              )}

              <CategoryModal
                value={null}
                onSelect={addCategory}
                trigger={
                  <Button variant='outline' size='sm' mt='2'>
                    Add category…
                  </Button>
                }
              />
            </VStack>
          )}
        </ModalBody>
        <ModalFooter gap='2'>
          <Button variant='outline' onClick={onClose}>
            Cancel
          </Button>
          <Button colorScheme='red' onClick={save} isLoading={replaceApi.isLoading} isDisabled={isLoadingAny}>
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
